import type { Middleware } from "openapi-fetch";

import { getOrStartRefresh } from "./sessionRefresh";
import { tokenStorage } from "./tokenStorage";

const RETRY_HEADER = "X-Smarta-Retry-After-Refresh";

/*
 * Shared openapi-fetch middleware for clients that hit the smarta-accounts
 * gateway (banking, documents). Mirrors the axios interceptor in BaseApi
 * and the Apollo errorLink in graphql/client.ts:
 *
 *  - onRequest: attach the current access token as Bearer
 *  - onResponse: on a 401, await `getOrStartRefresh` (concurrent 401s
 *    collapse to a single refresh) and replay the original request with
 *    the rotated token. The retry tags itself with a marker header so a
 *    second 401 falls through to the caller instead of looping.
 *
 * Refresh-token rejection emits `sessionEvents.session:expired` (handled
 * inside getOrStartRefresh) — the modal listener takes over. We swallow
 * the error here and return undefined so the original 401 propagates to
 * react-query as a normal failure.
 */
export const createAuthMiddleware = (): Middleware => {
  const requestClones = new Map<string, Request>();

  return {
    onRequest: async ({ request, id }) => {
      const accessToken = tokenStorage.getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      request.headers.set("Authorization", `Bearer ${accessToken}`);
      // Clone *before* fetch consumes the body so we can replay on 401.
      requestClones.set(id, request.clone());
      return request;
    },

    onResponse: async ({ request, response, id }) => {
      if (response.status !== 401) {
        requestClones.delete(id);
        return;
      }
      if (request.headers.get(RETRY_HEADER) === "1") {
        // Replay itself 401'd — fall through to the caller.
        requestClones.delete(id);
        return;
      }

      let tokens: Awaited<ReturnType<typeof getOrStartRefresh>>;
      try {
        tokens = await getOrStartRefresh();
      } catch {
        // Refresh failed — session:expired event already emitted.
        requestClones.delete(id);
        return;
      }

      const clone = requestClones.get(id);
      requestClones.delete(id);
      if (!clone) return;

      const retried = new Request(clone, {
        headers: new Headers(clone.headers),
      });
      retried.headers.set("Authorization", `Bearer ${tokens.accessToken}`);
      retried.headers.set(RETRY_HEADER, "1");
      return fetch(retried);
    },

    onError: async ({ id }) => {
      // fetch rejected (network error, timeout, abort) before any response —
      // onResponse never runs, so drop the clone here to avoid pinning it.
      requestClones.delete(id);
    },
  };
};
